import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Zap } from 'lucide-react';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';

interface ProcessingScreenProps {
  screen: Screen;
  onComplete?: () => void;
}

export const ProcessingScreen: React.FC<ProcessingScreenProps> = ({ screen, onComplete }) => {
  const [visibleStatusChecks, setVisibleStatusChecks] = useState<number[]>([]);

  useEffect(() => {
    // Show each status check individually in reverse order
    const statusCheckTimers: number[] = [];
    const totalChecks = screen.checks?.length || 0;
    
    // Reverse the order by iterating from last to first
    screen.checks?.forEach((_, index) => {
      const reverseIndex = totalChecks - 1 - index; // Reverse the index
      const timer = setTimeout(() => {
        setVisibleStatusChecks(prev => [...prev, reverseIndex]);
      }, 1500 + (index * 1500)); // Start at 2s, then every 2s
      statusCheckTimers.push(timer);
    });

    // Navigate to success screen after all checks are shown + 2 seconds
    const navigationTimer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, 1500 + (totalChecks * 1500) + 1000); // 2s + (checks * 2s) + 1.5s buffer

    return () => {
      statusCheckTimers.forEach(timer => clearTimeout(timer));
      clearTimeout(navigationTimer);
    };
  }, [onComplete, screen.checks]);

  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Processing */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            <div className="p-6 h-full flex flex-col items-center justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"
                />
              </motion.div>
              
              <h2 className="text-xl font-bold text-black mb-2">
                Processing...
              </h2>
              <p className="text-sm text-gray-600 text-center">
                Your account is being set up
              </p>
            </div>
          </PhoneFrame>
        </motion.div>

        {/* Right Side - Processing Status */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {screen.description}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              {screen.steps?.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex items-center space-x-2">
                    <div className={`step-dot ${step.status}`} />
                    <span className={`text-sm font-medium ${
                      step.status === 'active' ? 'text-blue-600' : 
                      step.status === 'completed' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < (screen.steps?.length || 0) - 1 && (
                    <div className="w-8 h-px bg-gray-300" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Demo Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">
              Slowed down for demonstration purposes
            </span>
          </motion.div>

          {/* Status Checks */}
          <div className="space-y-3">
            {screen.checks?.map((check, index) => {
              const isVisible = visibleStatusChecks.includes(index);
              return isVisible ? (
                <motion.div
                  key={check.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                    {check.icon === 'plus' && <span className="text-gray-600 font-bold">+</span>}
                    {check.icon === 'shield' && <span className="text-gray-600">🛡️</span>}
                    {check.icon === 'user-check' && <span className="text-gray-600">👤</span>}
                  </div>
                  <span className="font-medium text-gray-900">{check.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    check.status === 'completed' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {check.status === 'completed' ? 'Completed' : 'Submitted'}
                  </span>
                  <Zap className="w-4 h-4 text-yellow-500" />
                </div>
                </motion.div>
              ) : null;
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
